import type { PlaneCoordinate } from "./CartesianPlane";
import type { DeckBoardSegment } from "./DeckBoardsLayer";
import type { ConnectorPoint, JoistSegment, SupportPoint } from "./deckBottomViewGeometry";

type DxfPoint = {
  x: number;
  y: number;
};

type DxfLayer = {
  color: number;
  name: string;
};

type DxfBounds = {
  maxX: number;
  maxY: number;
  minX: number;
  minY: number;
};

type DeckBottomViewDxfLegendLabels = {
  boards: string;
  joists: string;
  supports: string;
};

type DeckBottomViewDxfInput = {
  boards: DeckBoardSegment[];
  joists: JoistSegment[];
  joistWidthMeters: number;
  legendLabels: DeckBottomViewDxfLegendLabels;
  outlineCoordinates: PlaneCoordinate[];
  supports: SupportPoint[];
};

type DeckConnectorsViewDxfLegendLabels = {
  boards: string;
  connectors: string;
  joists: string;
};

type DeckConnectorsViewDxfInput = {
  boards: DeckBoardSegment[];
  connectors: ConnectorPoint[];
  joists: JoistSegment[];
  joistWidthMeters: number;
  legendLabels: DeckConnectorsViewDxfLegendLabels;
  outlineCoordinates: PlaneCoordinate[];
};

const MILLIMETERS_PER_METER = 1000;
const SUPPORT_CROSS_HALF_SIZE_MM = 70;
const DXF_MIME_TYPE = "application/dxf";
const LEGEND_MARGIN_MM = 700;
const LEGEND_ROW_GAP_MM = 280;
const LEGEND_TEXT_OFFSET_MM = 560;
const LEGEND_TEXT_HEIGHT_MM = 120;
const LEGEND_SYMBOL_WIDTH_MM = 420;
const LEGEND_BOARD_SYMBOL_HEIGHT_MM = 120;
const LEGEND_JOIST_SYMBOL_HEIGHT_MM = 80;

const LAYERS = {
  boards: { name: "DECK_BOARDS", color: 3 },
  joists: { name: "SUBSTRUCTURE_JOISTS", color: 8 },
  supports: { name: "SUPPORT_POINTS", color: 2 },
  connectors: { name: "CONNECTOR_POINTS", color: 6 },
  legend: { name: "LEGEND", color: 7 },
} satisfies Record<string, DxfLayer>;

const formatDxfNumber = (value: number): string => {
  const roundedValue = Math.abs(value) < 0.0000001 ? 0 : Math.round(value * 1000) / 1000;

  return Number.isInteger(roundedValue) ? `${roundedValue}` : `${roundedValue}`.replace(/(\.\d*?)0+$/, "$1");
};

const metersToMillimeters = (coordinate: PlaneCoordinate): DxfPoint => ({
  x: coordinate.x * MILLIMETERS_PER_METER,
  y: coordinate.y * MILLIMETERS_PER_METER,
});

const pushPair = (lines: string[], code: number, value: string | number): void => {
  lines.push(`${code}`, typeof value === "number" ? formatDxfNumber(value) : value);
};

const pushSectionStart = (lines: string[], sectionName: string): void => {
  pushPair(lines, 0, "SECTION");
  pushPair(lines, 2, sectionName);
};

const pushSectionEnd = (lines: string[]): void => {
  pushPair(lines, 0, "ENDSEC");
};

const pushTableStart = (lines: string[], tableName: string, entryCount: number): void => {
  pushPair(lines, 0, "TABLE");
  pushPair(lines, 2, tableName);
  pushPair(lines, 100, "AcDbSymbolTable");
  pushPair(lines, 70, entryCount);
};

const pushTableEnd = (lines: string[]): void => {
  pushPair(lines, 0, "ENDTAB");
};

const pushHeader = (lines: string[]): void => {
  pushSectionStart(lines, "HEADER");
  pushPair(lines, 9, "$ACADVER");
  pushPair(lines, 1, "AC1015");
  pushPair(lines, 9, "$INSUNITS");
  pushPair(lines, 70, 4);
  pushPair(lines, 9, "$MEASUREMENT");
  pushPair(lines, 70, 1);
  pushSectionEnd(lines);
};

const pushTables = (lines: string[], layers: DxfLayer[]): void => {
  pushSectionStart(lines, "TABLES");

  pushTableStart(lines, "LTYPE", 1);
  pushPair(lines, 0, "LTYPE");
  pushPair(lines, 100, "AcDbSymbolTableRecord");
  pushPair(lines, 100, "AcDbLinetypeTableRecord");
  pushPair(lines, 2, "Continuous");
  pushPair(lines, 70, 0);
  pushPair(lines, 3, "Solid line");
  pushPair(lines, 72, 65);
  pushPair(lines, 73, 0);
  pushPair(lines, 40, 0);
  pushTableEnd(lines);

  pushTableStart(lines, "LAYER", layers.length + 1);
  pushLayer(lines, { name: "0", color: 7 });
  layers.forEach((layer) => pushLayer(lines, layer));
  pushTableEnd(lines);

  pushTableStart(lines, "APPID", 1);
  pushPair(lines, 0, "APPID");
  pushPair(lines, 100, "AcDbSymbolTableRecord");
  pushPair(lines, 100, "AcDbRegAppTableRecord");
  pushPair(lines, 2, "ACAD");
  pushPair(lines, 70, 0);
  pushTableEnd(lines);

  pushSectionEnd(lines);
};

const pushLayer = (lines: string[], layer: DxfLayer): void => {
  pushPair(lines, 0, "LAYER");
  pushPair(lines, 100, "AcDbSymbolTableRecord");
  pushPair(lines, 100, "AcDbLayerTableRecord");
  pushPair(lines, 2, layer.name);
  pushPair(lines, 70, 0);
  pushPair(lines, 62, layer.color);
  pushPair(lines, 6, "Continuous");
};

const pushLine = (lines: string[], layerName: string, start: DxfPoint, end: DxfPoint): void => {
  pushPair(lines, 0, "LINE");
  pushPair(lines, 100, "AcDbEntity");
  pushPair(lines, 8, layerName);
  pushPair(lines, 100, "AcDbLine");
  pushPair(lines, 10, start.x);
  pushPair(lines, 20, start.y);
  pushPair(lines, 30, 0);
  pushPair(lines, 11, end.x);
  pushPair(lines, 21, end.y);
  pushPair(lines, 31, 0);
};

const sanitizeDxfText = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7e]/g, "?")
    .replace(/[\r\n]+/g, " ");

const pushText = (lines: string[], layerName: string, point: DxfPoint, text: string, height: number): void => {
  pushPair(lines, 0, "TEXT");
  pushPair(lines, 100, "AcDbEntity");
  pushPair(lines, 8, layerName);
  pushPair(lines, 100, "AcDbText");
  pushPair(lines, 10, point.x);
  pushPair(lines, 20, point.y);
  pushPair(lines, 30, 0);
  pushPair(lines, 40, height);
  pushPair(lines, 1, sanitizeDxfText(text));
  pushPair(lines, 50, 0);
  pushPair(lines, 100, "AcDbText");
  pushPair(lines, 72, 0);
  pushPair(lines, 73, 0);
};

const pushClosedPolyline = (lines: string[], layerName: string, points: DxfPoint[]): void => {
  if (points.length < 3) {
    return;
  }

  pushPair(lines, 0, "LWPOLYLINE");
  pushPair(lines, 100, "AcDbEntity");
  pushPair(lines, 8, layerName);
  pushPair(lines, 100, "AcDbPolyline");
  pushPair(lines, 90, points.length);
  pushPair(lines, 70, 1);

  points.forEach((point) => {
    pushPair(lines, 10, point.x);
    pushPair(lines, 20, point.y);
  });
};

const pushCrossMarker = (
  lines: string[],
  layerName: string,
  center: DxfPoint,
  halfSize = SUPPORT_CROSS_HALF_SIZE_MM,
): void => {
  pushLine(
    lines,
    layerName,
    { x: center.x - halfSize, y: center.y },
    { x: center.x + halfSize, y: center.y },
  );
  pushLine(
    lines,
    layerName,
    { x: center.x, y: center.y - halfSize },
    { x: center.x, y: center.y + halfSize },
  );
};

const pushSupportMarker = (lines: string[], support: SupportPoint): void => {
  pushCrossMarker(lines, LAYERS.supports.name, metersToMillimeters(support));
};

const pushConnectorMarker = (lines: string[], connector: ConnectorPoint): void => {
  pushCrossMarker(lines, LAYERS.connectors.name, metersToMillimeters(connector));
};

const getJoistPolygonPoints = (joist: JoistSegment, joistWidthMeters: number): DxfPoint[] => {
  const start = metersToMillimeters(joist.start);
  const end = metersToMillimeters(joist.end);
  const width = joistWidthMeters * MILLIMETERS_PER_METER;
  const length = Math.hypot(end.x - start.x, end.y - start.y);

  if (width <= 0 || length <= 0) {
    return [];
  }

  const perpendicularX = -((end.y - start.y) / length);
  const perpendicularY = (end.x - start.x) / length;
  const halfWidth = width / 2;
  const offset = {
    x: perpendicularX * halfWidth,
    y: perpendicularY * halfWidth,
  };

  return [
    { x: start.x + offset.x, y: start.y + offset.y },
    { x: end.x + offset.x, y: end.y + offset.y },
    { x: end.x - offset.x, y: end.y - offset.y },
    { x: start.x - offset.x, y: start.y - offset.y },
  ];
};

const getDxfBounds = (points: DxfPoint[]): DxfBounds | null => {
  const firstPoint = points[0];

  if (!firstPoint) {
    return null;
  }

  return points.reduce<DxfBounds>(
    (bounds, point) => ({
      maxX: Math.max(bounds.maxX, point.x),
      maxY: Math.max(bounds.maxY, point.y),
      minX: Math.min(bounds.minX, point.x),
      minY: Math.min(bounds.minY, point.y),
    }),
    {
      maxX: firstPoint.x,
      maxY: firstPoint.y,
      minX: firstPoint.x,
      minY: firstPoint.y,
    },
  );
};

const getRectanglePoints = (left: number, centerY: number, width: number, height: number): DxfPoint[] => {
  const top = centerY + height / 2;
  const bottom = centerY - height / 2;

  return [
    { x: left, y: top },
    { x: left + width, y: top },
    { x: left + width, y: bottom },
    { x: left, y: bottom },
  ];
};

const pushLegendRow = (
  lines: string[],
  label: string,
  layerName: string,
  left: number,
  centerY: number,
  symbolHeight: number,
): void => {
  pushClosedPolyline(
    lines,
    layerName,
    getRectanglePoints(left, centerY, LEGEND_SYMBOL_WIDTH_MM, symbolHeight),
  );
  pushText(
    lines,
    LAYERS.legend.name,
    { x: left + LEGEND_TEXT_OFFSET_MM, y: centerY - LEGEND_TEXT_HEIGHT_MM / 2 },
    label,
    LEGEND_TEXT_HEIGHT_MM,
  );
};

const pushCrossLegendRow = (lines: string[], label: string, layerName: string, left: number, centerY: number): void => {
  pushCrossMarker(lines, layerName, { x: left + LEGEND_SYMBOL_WIDTH_MM / 2, y: centerY }, SUPPORT_CROSS_HALF_SIZE_MM);
  pushText(
    lines,
    LAYERS.legend.name,
    { x: left + LEGEND_TEXT_OFFSET_MM, y: centerY - LEGEND_TEXT_HEIGHT_MM / 2 },
    label,
    LEGEND_TEXT_HEIGHT_MM,
  );
};

const pushBoardsAndJoistsLegendRows = (
  lines: string[],
  labels: { boards: string; joists: string },
  left: number,
  top: number,
): void => {
  pushLegendRow(lines, labels.boards, LAYERS.boards.name, left, top, LEGEND_BOARD_SYMBOL_HEIGHT_MM);
  pushLegendRow(lines, labels.joists, LAYERS.joists.name, left, top - LEGEND_ROW_GAP_MM, LEGEND_JOIST_SYMBOL_HEIGHT_MM);
};

const pushLegend = (
  lines: string[],
  labels: DeckBottomViewDxfLegendLabels,
  outlinePoints: DxfPoint[],
): void => {
  const bounds = getDxfBounds(outlinePoints);
  const left = (bounds?.maxX ?? 0) + LEGEND_MARGIN_MM;
  const top = bounds?.maxY ?? 0;

  pushBoardsAndJoistsLegendRows(lines, labels, left, top);
  pushCrossLegendRow(lines, labels.supports, LAYERS.supports.name, left, top - LEGEND_ROW_GAP_MM * 2);
};

const pushConnectorsLegend = (
  lines: string[],
  labels: DeckConnectorsViewDxfLegendLabels,
  outlinePoints: DxfPoint[],
): void => {
  const bounds = getDxfBounds(outlinePoints);
  const left = (bounds?.maxX ?? 0) + LEGEND_MARGIN_MM;
  const top = bounds?.maxY ?? 0;

  pushBoardsAndJoistsLegendRows(lines, labels, left, top);
  pushCrossLegendRow(lines, labels.connectors, LAYERS.connectors.name, left, top - LEGEND_ROW_GAP_MM * 2);
};

export const createDeckBottomViewDxf = ({
  boards,
  joists,
  joistWidthMeters,
  legendLabels,
  outlineCoordinates,
  supports,
}: DeckBottomViewDxfInput): string => {
  const lines: string[] = [];
  const layers = Object.values(LAYERS);

  pushHeader(lines);
  pushTables(lines, layers);
  pushSectionStart(lines, "ENTITIES");

  const outlinePoints = outlineCoordinates.map(metersToMillimeters);
  const joistPolygons = joists.map((joist) => getJoistPolygonPoints(joist, joistWidthMeters));

  boards.forEach((board) => {
    pushClosedPolyline(lines, LAYERS.boards.name, board.corners.map(metersToMillimeters));
  });
  joistPolygons.forEach((joistPoints) => pushClosedPolyline(lines, LAYERS.joists.name, joistPoints));
  supports.forEach((support) => pushSupportMarker(lines, support));
  pushLegend(lines, legendLabels, outlinePoints);

  pushSectionEnd(lines);
  pushPair(lines, 0, "EOF");

  return `${lines.join("\n")}\n`;
};

export const createDeckConnectorsViewDxf = ({
  boards,
  connectors,
  joists,
  joistWidthMeters,
  legendLabels,
  outlineCoordinates,
}: DeckConnectorsViewDxfInput): string => {
  const lines: string[] = [];
  const layers = Object.values(LAYERS);

  pushHeader(lines);
  pushTables(lines, layers);
  pushSectionStart(lines, "ENTITIES");

  const outlinePoints = outlineCoordinates.map(metersToMillimeters);
  const joistPolygons = joists.map((joist) => getJoistPolygonPoints(joist, joistWidthMeters));

  boards.forEach((board) => {
    pushClosedPolyline(lines, LAYERS.boards.name, board.corners.map(metersToMillimeters));
  });
  joistPolygons.forEach((joistPoints) => pushClosedPolyline(lines, LAYERS.joists.name, joistPoints));
  connectors.forEach((connector) => pushConnectorMarker(lines, connector));
  pushConnectorsLegend(lines, legendLabels, outlinePoints);

  pushSectionEnd(lines);
  pushPair(lines, 0, "EOF");

  return `${lines.join("\n")}\n`;
};

const DEFAULT_DXF_FILE_NAME = "deck";

const sanitizeDxfFileNameSegment = (value: string): string =>
  value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export const getDeckDxfFileName = (configurationName: string | null | undefined, suffix: string): string => {
  const sanitizedName = configurationName ? sanitizeDxfFileNameSegment(configurationName) : "";

  return `${sanitizedName || DEFAULT_DXF_FILE_NAME}-${suffix}.dxf`;
};

export const downloadDxfFile = (content: string, fileName: string): void => {
  const blob = new Blob([content], { type: `${DXF_MIME_TYPE};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};
